import { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAdditionalFormContext } from '../../../common/form';
import { encodeStringKey } from '../../../common/utils';
import { AlgorithmSourceDimensionFormDataType } from '../sources/schema/types';
import {
  AlgorithmJoinItemSelectField,
  AlgorithmJoinItemSelectFieldProps,
} from './AlgorithmJoinItemSelectField';
import { useAlgorithmSourceDimensionCategoriesQuery } from './query/useDimensionCategoriesQuery';
import { AlgorithmJoinsValidFormDataType } from './schema/types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmJoinItemCategorySelectField.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-category-select-field',
);
addTranslations(translations);

export interface AlgorithmJoinItemCategorySelectFieldProps
  extends Omit<
    AlgorithmJoinItemSelectFieldProps,
    'name' | 'label' | 'value' | 'options' | 'onSelect' | 'onClear'
  > {
  joinIndex: number;
  index: number;
  role: 'source' | 'target';
  dimension: AlgorithmSourceDimensionFormDataType;
}

export const AlgorithmJoinItemCategorySelectField: FC<
  AlgorithmJoinItemCategorySelectFieldProps
> = (props) => {
  const { joinIndex, index, role, dimension, ...rest } = props;
  const { t } = useTranslation();
  const { data, isFetching } =
    useAlgorithmSourceDimensionCategoriesQuery(dimension);

  const options = useMemo(
    () =>
      (data || []).map((category) => {
        const { code, name } = category;
        const key = encodeStringKey('category', code);
        return {
          key,
          value: key,
          label: `${code} - ${name}`,
          displayLabel: `${code} - ${name}`,
          category,
        };
      }),
    [data],
  );

  const { setValue } = useFormContext<AlgorithmJoinsValidFormDataType>();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const name = `joins.${joinIndex}.algorithmJoinItems.${index}` as const;
  const value = useWatch<AlgorithmJoinsValidFormDataType, typeof name>({
    name,
  });

  const selectedValue = useMemo(() => {
    if (role === 'source' && 'sourceValue' in value) {
      return value.sourceValue
        ? encodeStringKey('category', value.sourceValue)
        : '';
    }
    if (role === 'target' && 'targetValue' in value) {
      return value.targetValue
        ? encodeStringKey('category', value.targetValue)
        : '';
    }
    throw new Error(`Expecting "${role}Value" prop in ${name} value object`);
  }, [value, name, role]);

  const handleSelect: AlgorithmJoinItemSelectFieldProps['onSelect'] =
    useCallback(
      (_value, option) => {
        setValue(
          `${name}.${role === 'source' ? 'sourceValue' : 'targetValue'}`,
          option.category.code,
          { shouldDirty: true },
        );
        formOnChange?.();
      },
      [name, setValue],
    );

  const handleClear: AlgorithmJoinItemSelectFieldProps['onClear'] =
    useCallback(() => {
      setValue(
        `${name}.${role === 'source' ? 'sourceValue' : 'targetValue'}`,
        '',
        { shouldDirty: true },
      );
      formOnChange?.();
    }, [name, setValue]);

  return (
    <AlgorithmJoinItemSelectField
      name={`${name}.sourceValue`}
      label={t(`${role}-category`)}
      value={selectedValue}
      onSelect={handleSelect}
      onClear={handleClear}
      options={options}
      loading={isFetching}
      disabled={isFetching}
      {...rest}
    />
  );
};
